/**
 * Matcora Widget Loader
 *
 * Drop-in loader for embedding the companion widget on any page.
 * Fetches per-tenant config from Front Door, loads the versioned UMD bundle,
 * and auto-initializes the widget — all from a single `<script>` tag.
 *
 * Merchant embed (FAB mode, one line):
 * ```html
 * <script src="https://widget.matcora.com/widget/loader.js" data-dpt-slot="01"></script>
 * ```
 *
 * Full-page mode on a dedicated page:
 * ```html
 * <script src="https://widget.matcora.com/widget/loader.js" data-dpt-slot="01" data-mode="fullpage"></script>
 * ```
 *
 * Production tenant:
 * ```html
 * <script src="https://widget.matcora.com/widget/loader.js" data-tenant="tp_xxx"></script>
 * ```
 *
 * Attributes:
 *   data-tenant       Tenant public ID (e.g. "tp_xxx"). Mutually exclusive with data-dpt-slot.
 *   data-dpt-slot     DPT slot ID (e.g. "01"). Mutually exclusive with data-tenant.
 *   data-mode         Widget display mode: "fab" (default) or "fullpage".
 *   data-autoinit     Set to "false" to skip auto-initialization (loader only fetches config + loads UMD).
 *   data-install-token  Day-2 install token for merchant validation.
 *   data-preview-token  HMAC-signed preview token for Level 1 preview access.
 *
 * FAIL-CLOSED: Any error prevents the widget from loading.
 * Errors are logged to console but never thrown — the host page is never broken.
 */

import { loadTenantConfig, loadDptSlotConfig } from '../core/runtime-config';
import type { RuntimeConfig, CompanionMessagesConfig, ExperimentVariant } from '../core/runtime-config';
import { createTenantStorage, STORAGE_KEYS } from './core/WidgetStorage';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const LOG_PREFIX = '[MatcoraWidget]';
const UMD_SCRIPT_ID = 'matcora-widget-umd';

/** Tenant public ID format: tp_ followed by alphanumeric characters. */
const TP_PATTERN = /^tp_[a-zA-Z0-9]+$/;

/** DPT slot ID: 2-digit "01".."99" (excludes "00"). */
const SLOT_PATTERN = /^(0[1-9]|[1-9][0-9])$/;

// ---------------------------------------------------------------------------
// Sync capture — snapshot everything before any async work
// ---------------------------------------------------------------------------

/**
 * Capture the script element AND its dataset synchronously at module scope.
 * `document.currentScript` becomes `null` inside async callbacks, and
 * referencing `.dataset` later can be surprising if scripts are moved.
 * A plain object snapshot makes behavior deterministic.
 */
const LOADER_SCRIPT = document.currentScript as HTMLScriptElement | null;

const LOADER_INPUT = LOADER_SCRIPT
  ? {
      src: LOADER_SCRIPT.src,
      tenant: LOADER_SCRIPT.dataset.tenant?.trim() || '',
      dptSlot: LOADER_SCRIPT.dataset.dptSlot?.trim() || '',
      modeRaw: (LOADER_SCRIPT.dataset.mode || 'fab').trim().toLowerCase(),
      autoinit:
        (LOADER_SCRIPT.dataset.autoinit ?? 'true').trim().toLowerCase() !== 'false',
      installToken: LOADER_SCRIPT.dataset.installToken?.trim() || null,
      previewToken: LOADER_SCRIPT.dataset.previewToken?.trim() || null,
    }
  : null;

declare global {
  interface Window {
    __MATCORA_CONFIG__?: RuntimeConfig;
    __MATCORA_INSTALL__?: { installToken: string | null };
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Derive CDN origin from the loader script's own URL. */
function cdnOrigin(): string {
  return new URL(LOADER_INPUT!.src).origin;
}

/** Validate the snapshot and return resolved addressing + mode. */
function validateInput() {
  const input = LOADER_INPUT!;
  const tenant = input.tenant || undefined;
  const slot = input.dptSlot || undefined;

  // Exactly one addressing mode
  if (tenant && slot) {
    throw new Error(
      `Both data-tenant="${tenant}" and data-dpt-slot="${slot}" are set. ` +
      'Use one or the other, not both.',
    );
  }
  if (!tenant && !slot) {
    throw new Error(
      'Missing addressing attribute. Provide exactly one of:\n' +
      '  data-tenant="tp_xxx"   (merchant / production)\n' +
      '  data-dpt-slot="01"     (DP-test slot)',
    );
  }

  // Validate format
  if (tenant && !TP_PATTERN.test(tenant)) {
    throw new Error(
      `Invalid data-tenant value: "${tenant}" (expected tp_ prefix with alphanumeric ID)`,
    );
  }
  if (slot && !SLOT_PATTERN.test(slot)) {
    throw new Error(
      `Invalid data-dpt-slot value: "${slot}" (expected 2-digit slot ID: "01".."99")`,
    );
  }

  // Normalize mode — accept "full" or "fullpage"
  const mode: 'fab' | 'fullpage' =
    input.modeRaw === 'full' || input.modeRaw === 'fullpage' ? 'fullpage' : 'fab';

  return { tenant, slot, mode, autoinit: input.autoinit, installToken: input.installToken, previewToken: input.previewToken };
}

/**
 * In-flight dedup map for loadScriptOnce.
 * Prevents concurrent calls from loading the same script twice.
 */
const scriptPromises = new Map<string, Promise<void>>();

/**
 * Idempotent script loader with DOM id for dedup.
 * If a script with the same `id` and `src` already exists, resolves immediately.
 * If `id` exists but `src` differs, removes the old script and loads the new one.
 * Concurrent calls for the same `id` share a single promise.
 */
function loadScriptOnce(id: string, src: string): Promise<void> {
  // Dedup in-flight loads
  const inflight = scriptPromises.get(id);
  if (inflight) return inflight;

  const existing = document.getElementById(id) as HTMLScriptElement | null;
  if (existing) {
    if (existing.src === src) return Promise.resolve();
    existing.remove();
  }

  const promise = new Promise<void>((resolve, reject) => {
    const s = document.createElement('script');
    s.id = id;
    s.src = src;
    s.async = true;
    s.onload = () => {
      scriptPromises.delete(id);
      resolve();
    };
    s.onerror = () => {
      scriptPromises.delete(id);
      reject(new Error(`Failed to load script: ${src}`));
    };
    document.head.appendChild(s);
  });

  scriptPromises.set(id, promise);
  return promise;
}

// ---------------------------------------------------------------------------
// Companion Messages (non-blocking, fail-safe)
// ---------------------------------------------------------------------------

/**
 * Fetch companion-messages.json from the branding assets path.
 * Returns null on any error — the widget works fine with generic fallbacks.
 */
async function fetchCompanionMessages(
  brandingAssetsBaseUrl: string,
): Promise<CompanionMessagesConfig | null> {
  try {
    const base = brandingAssetsBaseUrl.endsWith('/')
      ? brandingAssetsBaseUrl
      : brandingAssetsBaseUrl + '/';
    const url = `${base}companion-messages.json`;

    const response = await fetch(url, { cache: 'no-store' });
    if (!response.ok) return null;

    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) return null;

    const data = await response.json();

    // Basic shape validation — all fields optional
    if (typeof data !== 'object' || data === null || Array.isArray(data)) return null;
    if (data.tooltipMessage !== undefined && typeof data.tooltipMessage !== 'string') return null;
    if (data.greetings !== undefined && !Array.isArray(data.greetings)) return null;
    if (data.suggestions !== undefined && !Array.isArray(data.suggestions)) return null;
    if (data.openingSuggestions !== undefined && !Array.isArray(data.openingSuggestions)) return null;
    if (data.chatDisclaimer !== undefined && typeof data.chatDisclaimer !== 'string') return null;
    if (data.idleHint !== undefined && typeof data.idleHint !== 'string') return null;

    return data as CompanionMessagesConfig;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Experiment Resolution
// ---------------------------------------------------------------------------

interface ExperimentAssignment {
  experimentName: string;
  /** 'control' or the configured variantId (e.g. 'variant-b'). */
  variant: string;
}

/**
 * Resolve the experiment assignment for the current user.
 * Sticky: once assigned, the same variant is returned on subsequent visits.
 */
function resolveExperiment(
  config: RuntimeConfig,
): { resolvedConfig: RuntimeConfig; assignment: ExperimentAssignment | null } {
  if (!config.experiments) return { resolvedConfig: config, assignment: null };

  // Find the single enabled experiment (validation ensures at most one)
  const entry = Object.entries(config.experiments).find(([, exp]) => exp.enabled);
  if (!entry) return { resolvedConfig: config, assignment: null };

  const [experimentName, experiment] = entry;
  const storage = createTenantStorage(config.tenantPublicId);
  const storageKey = `${STORAGE_KEYS.EXPERIMENT_PREFIX}:${experimentName}`;

  // Sticky assignment: check localStorage first
  let variant = storage.get<string>(storageKey);
  if (!variant) {
    variant = Math.floor(Math.random() * 100) < experiment.splitPercent
      ? experiment.variantId
      : 'control';
    storage.set(storageKey, variant);
  }

  const assignment: ExperimentAssignment = { experimentName, variant };

  if (variant === 'control') {
    return { resolvedConfig: config, assignment };
  }

  // Apply variant overrides
  return { resolvedConfig: applyVariantOverrides(config, experiment.variant), assignment };
}

/**
 * Deep-merge variant overrides into a copy of the config.
 * Only merges explicitly supported paths (ui.display, ui.theme, branding.baseUrl).
 */
function applyVariantOverrides(
  config: RuntimeConfig,
  variant: ExperimentVariant,
): RuntimeConfig {
  const merged = { ...config };

  if (variant.ui) {
    merged.ui = { ...config.ui };
    if (variant.ui.display) {
      merged.ui.display = { ...config.ui?.display, ...variant.ui.display };
    }
    if (variant.ui.theme) {
      merged.ui.theme = {
        ...config.ui?.theme,
        ...variant.ui.theme,
        ...(variant.ui.theme.chat && {
          chat: { ...config.ui?.theme?.chat, ...variant.ui.theme.chat },
        }),
      };
    }
  }

  // Variant branding: only override baseUrl (for companion-messages.json loading)
  if (variant.branding?.baseUrl && config.branding) {
    merged.branding = { ...config.branding, baseUrl: variant.branding.baseUrl };
  }

  return merged;
}

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------

(async function main() {
  try {
    // 1. Guard: loader script must exist
    if (!LOADER_SCRIPT || !LOADER_INPUT) {
      throw new Error(
        'Could not detect loader <script> element. ' +
        'Ensure the loader is included via a regular <script src="..."> tag (not type="module").',
      );
    }

    // 2. Validate snapshot and resolve addressing + mode
    const { tenant, slot, mode, autoinit, installToken, previewToken } = validateInput();

    // 3. Capture install token (Day 2 — captured now to avoid snippet changes later)
    window.__MATCORA_INSTALL__ = { installToken };

    // 4. Fetch config
    const origin = cdnOrigin();
    const config: RuntimeConfig = tenant
      ? await loadTenantConfig(tenant, origin)
      : await loadDptSlotConfig(slot!, origin);

    // 5. Resolve experiment (sticky assignment via localStorage)
    const { resolvedConfig, assignment } = resolveExperiment(config);

    // 5b. Expose resolved config for consumers
    window.__MATCORA_CONFIG__ = resolvedConfig;

    // 6. Load the versioned widget UMD bundle
    const widgetUrl = `${resolvedConfig.assetsBaseUrl}/companion-widget.umd.js`;

    const isLocalDev =
      widgetUrl.startsWith('http://localhost') ||
      widgetUrl.startsWith('http://127.0.0.1');
    if (!isLocalDev && !widgetUrl.startsWith('https://')) {
      throw new Error(`Widget bundle URL must be HTTPS: ${widgetUrl}`);
    }

    // 6b. Fetch companion messages in parallel (non-blocking, fail-safe)
    // Uses resolvedConfig.branding.baseUrl — may be variant branding URL
    const messagesPromise = resolvedConfig.branding?.baseUrl
      ? fetchCompanionMessages(resolvedConfig.branding.baseUrl)
      : Promise.resolve(null);

    const [, companionMessages] = await Promise.all([
      loadScriptOnce(UMD_SCRIPT_ID, widgetUrl),
      messagesPromise,
    ]);

    // 7. Auto-init unless disabled
    if (!autoinit) {
      console.log(`${LOG_PREFIX} UMD loaded. Auto-init disabled (data-autoinit="false").`);
      return;
    }

    const win = window as unknown as Record<string, unknown>;
    const CW = win.CompanionWidget as
      | { init: (options: Record<string, unknown>) => unknown; [k: string]: unknown }
      | undefined;
    if (!CW?.init || typeof CW.init !== 'function') {
      throw new Error(
        'CompanionWidget.init not available after UMD load. ' +
        'Check that the widget bundle version matches the loader version.',
      );
    }

    const display = resolvedConfig.ui?.display;

    CW.init({
      apiBaseUrl: resolvedConfig.apiBaseUrl,
      mode,
      // Branding base URL for asset resolution
      ...(resolvedConfig.branding?.baseUrl && { brandingBaseUrl: resolvedConfig.branding.baseUrl }),
      // Avatar: resolve branding path to full URL
      ...(resolvedConfig.branding?.companionAvatarPath && {
        avatarImageUrl: `${resolvedConfig.branding.baseUrl}${resolvedConfig.branding.companionAvatarPath}`,
      }),
      // Display: bridge config display names → internal fields
      ...(display?.brandName && { brandName: display.brandName }),
      ...(display?.companionName && { companionName: display.companionName }),
      ...(display?.aboutBrandUrl && { aboutBrandUrl: display.aboutBrandUrl }),
      // Experiment display overrides
      ...(display?.headerTitle && { headerTitle: display.headerTitle }),
      ...(display?.headerSubtitle && { headerSubtitle: display.headerSubtitle }),
      ...(display?.fabStyle && { fabStyle: display.fabStyle }),
      ...(display?.showChatAvatar !== undefined && { showChatAvatar: display.showChatAvatar }),
      // Theme — pass full block for ThemeManager
      ...(resolvedConfig.ui?.theme && { themeConfig: resolvedConfig.ui.theme }),
      // Companion messages (greetings, suggestions, tooltip, idle hint)
      ...(companionMessages && { companionMessages }),
      // Feature flags
      ...(resolvedConfig.features && { features: resolvedConfig.features }),
      // Preview token — signed bearer token for Level 1 preview access
      ...(previewToken && { previewToken }),
      // Experiment info (for analytics headers)
      ...(assignment && { experimentName: assignment.experimentName }),
      ...(assignment && { experimentVariant: assignment.variant }),
    });

    const variantInfo = assignment
      ? ` experiment=${assignment.experimentName} variant=${assignment.variant}`
      : '';
    console.log(`${LOG_PREFIX} Widget initialized (mode=${mode}${variantInfo}).`);
  } catch (error) {
    // Fail closed: log but never throw — the host page must not break.
    console.error(`${LOG_PREFIX} Widget loading failed:`, error);
  }
})();
